import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, Video, Link2, Users, Trash2, Download, Send, 
  ExternalLink, Copy, Eye, Search, RefreshCw, Loader2,
  Play, CheckCircle, XCircle
} from 'lucide-react';

export default function VideosPage() {
  const { api } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewersDialog, setShowViewersDialog] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [viewers, setViewers] = useState([]);
  const [viewerStats, setViewerStats] = useState(null);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [selectedViewers, setSelectedViewers] = useState([]);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  
  // Create form
  const [createForm, setCreateForm] = useState({
    name: '',
    youtube_url: '',
    committee_name: ''
  });
  const [creating, setCreating] = useState(false);

  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/videos');
      setVideos(response.data.videos || []);
    } catch (error) {
      toast.error('Failed to fetch videos');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const handleCreateVideo = async () => {
    if (!createForm.name || !createForm.youtube_url || !createForm.committee_name) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      setCreating(true);
      const response = await api.post('/videos', createForm);
      toast.success('Video created successfully!');
      setShowCreateDialog(false);
      setCreateForm({ name: '', youtube_url: '', committee_name: '' });
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create video');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await api.delete(`/videos/${videoId}`);
      toast.success('Video deleted');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleToggleStatus = async (videoId) => {
    try {
      const response = await api.put(`/videos/${videoId}/toggle`);
      toast.success(response.data.message);
      fetchVideos();
    } catch (error) {
      toast.error('Failed to toggle video status');
    }
  };

  const fetchViewers = async (video) => {
    setSelectedVideo(video);
    setShowViewersDialog(true);
    setViewersLoading(true);
    setSelectedViewers([]);
    setViewerStats(null);
    
    try {
      const response = await api.get(`/videos/${video.id}/viewers`);
      setViewers(response.data.viewers || []);
      setViewerStats(response.data.stats || null);
    } catch (error) {
      toast.error('Failed to fetch viewers');
    } finally {
      setViewersLoading(false);
    }
  };

  const handleExport = async (format) => {
    if (!selectedVideo) return;
    
    try {
      const response = await api.get(`/videos/${selectedVideo.id}/export?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `video_viewers_${selectedVideo.name.slice(0, 20)}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleSendWhatsApp = async (sendAll = false) => {
    if (!selectedVideo) return;
    
    const viewerIds = sendAll ? [] : selectedViewers;
    if (!sendAll && viewerIds.length === 0) {
      toast.error('Please select viewers to send messages to');
      return;
    }
    
    try {
      setSendingWhatsApp(true);
      const params = new URLSearchParams();
      if (sendAll) params.append('send_all', 'true');
      viewerIds.forEach(id => params.append('viewer_ids', id));
      
      const response = await api.post(`/videos/${selectedVideo.id}/send-whatsapp?${params}`);
      toast.success(`Sent: ${response.data.sent}, Failed: ${response.data.failed}`);
      
      // Refresh viewers
      fetchViewers(selectedVideo);
    } catch (error) {
      toast.error('Failed to send WhatsApp messages');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const copyPublicLink = (publicCode) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/video/${publicCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Public link copied to clipboard!');
  };

  const toggleViewerSelection = (viewerId) => {
    setSelectedViewers(prev => 
      prev.includes(viewerId) 
        ? prev.filter(id => id !== viewerId)
        : [...prev, viewerId]
    );
  };

  const selectAllViewers = () => {
    if (selectedViewers.length === viewers.length) {
      setSelectedViewers([]);
    } else {
      setSelectedViewers(viewers.map(v => v.id));
    }
  };

  return (
    <div className="space-y-6" data-testid="videos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Video Management</h1>
          <p className="text-slate-500">Create public video links and track viewers</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-video-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Video
        </Button>
      </div>

      {/* Videos List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-navy" />
        </div>
      ) : videos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Video className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-500">No videos yet. Create your first video!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {videos.map((video) => (
            <Card key={video.id} className="hover:shadow-md transition-shadow" data-testid={`video-card-${video.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Video Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <Play className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <h3 className="font-semibold text-lg text-slate-800 truncate">{video.name}</h3>
                      <Badge variant={video.is_active ? "default" : "secondary"}>
                        {video.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500 mb-1">Committee: {video.committee_name}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {video.total_views || 0} views
                      </span>
                      <span className="flex items-center gap-1">
                        <Link2 className="w-4 h-4" />
                        Code: {video.public_code}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyPublicLink(video.public_code)}
                      data-testid={`copy-link-${video.id}`}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchViewers(video)}
                      data-testid={`view-viewers-${video.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Viewers
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(video.youtube_url, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      YouTube
                    </Button>
                    <Button
                      variant={video.is_active ? "secondary" : "default"}
                      size="sm"
                      onClick={() => handleToggleStatus(video.id)}
                    >
                      {video.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteVideo(video.id)}
                      data-testid={`delete-video-${video.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Video Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Video</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="video-name">Video Name *</Label>
              <Input
                id="video-name"
                placeholder="Enter video name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                data-testid="video-name-input"
              />
            </div>
            <div>
              <Label htmlFor="youtube-url">YouTube URL *</Label>
              <Input
                id="youtube-url"
                placeholder="https://youtube.com/watch?v=..."
                value={createForm.youtube_url}
                onChange={(e) => setCreateForm({ ...createForm, youtube_url: e.target.value })}
                data-testid="youtube-url-input"
              />
            </div>
            <div>
              <Label htmlFor="committee-name">Committee Name *</Label>
              <Input
                id="committee-name"
                placeholder="Enter committee name"
                value={createForm.committee_name}
                onChange={(e) => setCreateForm({ ...createForm, committee_name: e.target.value })}
                data-testid="committee-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateVideo} disabled={creating} data-testid="create-video-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Viewers Dialog */}
      <Dialog open={showViewersDialog} onOpenChange={setShowViewersDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Viewers - {selectedVideo?.name}
            </DialogTitle>
          </DialogHeader>
          
          {/* Statistics Bar */}
          {viewerStats && (
            <div className="flex flex-wrap items-center gap-4 py-3 px-4 bg-slate-50 rounded-lg mb-2">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-500" />
                <span className="text-sm"><strong>Total Views:</strong> {viewerStats.total_views}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-green-500" />
                <span className="text-sm"><strong>Unique Viewers:</strong> {viewerStats.unique_viewers}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-orange-600"><strong>Duplicate ITS:</strong> {viewerStats.duplicate_its_count}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-red-600"><strong>Duplicate Views:</strong> {viewerStats.duplicate_views}</span>
              </div>
            </div>
          )}
          
          {/* Actions Bar */}
          <div className="flex flex-wrap items-center gap-2 py-2 border-b">
            <Button variant="outline" size="sm" onClick={() => handleExport('excel')}>
              <Download className="w-4 h-4 mr-1" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('pdf')}>
              <Download className="w-4 h-4 mr-1" />
              PDF
            </Button>
            <div className="flex-1" />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleSendWhatsApp(false)}
              disabled={sendingWhatsApp || selectedViewers.length === 0}
            >
              {sendingWhatsApp ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Send to Selected ({selectedViewers.length})
            </Button>
            <Button 
              size="sm" 
              onClick={() => handleSendWhatsApp(true)}
              disabled={sendingWhatsApp || viewers.length === 0}
            >
              {sendingWhatsApp ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Send to All
            </Button>
          </div>

          {/* Viewers Table */}
          <div className="flex-1 overflow-auto">
            {viewersLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-navy" />
              </div>
            ) : viewers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No viewers yet for this video
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedViewers.length === viewers.length && viewers.length > 0}
                        onCheckedChange={selectAllViewers}
                      />
                    </TableHead>
                    <TableHead>ITS ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Mohalla</TableHead>
                    <TableHead>Watched At</TableHead>
                    <TableHead>WhatsApp</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewers.map((viewer) => (
                    <TableRow key={viewer.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedViewers.includes(viewer.id)}
                          onCheckedChange={() => toggleViewerSelection(viewer.id)}
                        />
                      </TableCell>
                      <TableCell className="font-mono">{viewer.its_id}</TableCell>
                      <TableCell>{viewer.member_name}</TableCell>
                      <TableCell>{viewer.mobile}</TableCell>
                      <TableCell>{viewer.mohalla}</TableCell>
                      <TableCell>
                        {viewer.watched_at ? new Date(viewer.watched_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </TableCell>
                      <TableCell>
                        {viewer.whatsapp_sent ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-slate-300" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
